import type { ApolloClient, InMemoryCache } from "@apollo/client";
import { useLazyQuery, useMutation } from "@apollo/client";
import { usePathname } from "next/navigation";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  accessTokenHasExpired,
  createRequestContext,
  getClient
} from "lib/apolloClient";
import { FeatureFlagContext } from "components/FeatureFlagProvider";
import * as GQL from "./queries";
import { datadogLogs } from "@datadog/browser-logs";
import { useUser } from "lib/user";

type Prompts = string[] | "SYNC" | "FAILURE" | undefined;
type UseUserPrompt = {
  shouldShowPrompt: (
    prompt: string,
    featureFlag?: { name: string; requiredValue: string | number | boolean }
  ) => boolean;
  markPromptAsSeen: (...prompt: string[]) => Promise<void>;
  showNotification?: boolean;
  setShowNotification?: (show: boolean) => void;
};

const USER_PROMPTS_STORAGE_KEY = "UserPrompts";
const PAGE_EXCLUSIONS = [/\/(us\/)?(basket|checkout|login|register).*?/];

export const status = { assessed: false };

function fromLocalStorage(): Prompts {
  const value = window.localStorage.getItem(USER_PROMPTS_STORAGE_KEY);
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value as Prompts;
  }
}

function toLocalStorage(currentPrompts: Prompts): void {
  if (currentPrompts) {
    window.localStorage.setItem(
      USER_PROMPTS_STORAGE_KEY,
      currentPrompts && Array.isArray(currentPrompts)
        ? JSON.stringify([...new Set(currentPrompts)])
        : (currentPrompts ?? "SYNC")
    );
  }
}

const apolloClientOptions = () => ({
  client: getClient() as ApolloClient<InMemoryCache>,
  context: createRequestContext({
    sessionStrategy: "START_NEW_ANONYMOUS"
  })
});

/* HOOK */
export const useUserPrompt = (): UseUserPrompt => {
  const { isLoggedIn } = useUser();
  const { ldClient } = useContext(FeatureFlagContext);
  const pathName = usePathname();
  const [currentPrompts, setCurrentPrompts] = useState<Prompts>();
  const [showNotification, setShowNotification] = useState<boolean>(false);

  const [getPromptsQuery, getPromptsState] = useLazyQuery(
    GQL.GET_USER_PROMPTS,
    apolloClientOptions()
  );
  const [addPromptMutation] = useMutation(
    GQL.ADD_UPS_USER_PROMPT,
    apolloClientOptions()
  );

  const promptNotSeenBefore = useCallback(
    (prompt: string): boolean => {
      return Array.isArray(currentPrompts) && !currentPrompts.includes(prompt);
    },
    [currentPrompts]
  );

  const isTargetedUserContext = useCallback((): boolean => {
    return isLoggedIn;
  }, [isLoggedIn]);

  const isNotExcludedPage = useCallback((): boolean => {
    return PAGE_EXCLUSIONS.every((exclusion) => !pathName.match(exclusion));
  }, [pathName]);

  useEffect(() => {
    if (!status.assessed) {
      const lsPrompts = fromLocalStorage();
      if (isTargetedUserContext()) {
        if (accessTokenHasExpired()) {
          setCurrentPrompts("SYNC");
        } else if (Array.isArray(lsPrompts)) {
          setCurrentPrompts(lsPrompts);
        } else if (lsPrompts !== "FAILURE") {
          if (
            isNotExcludedPage() &&
            !getPromptsState.loading &&
            !getPromptsState.error
          ) {
            getPromptsQuery();
          }
        }
      } else if (lsPrompts) {
        localStorage.removeItem(USER_PROMPTS_STORAGE_KEY);
      }
      status.assessed = true;
    }
  }, [
    getPromptsQuery,
    isNotExcludedPage,
    getPromptsState,
    isTargetedUserContext
  ]);

  useEffect(() => toLocalStorage(currentPrompts), [currentPrompts]);

  useEffect(() => {
    if (getPromptsState?.data || getPromptsState?.error) {
      const remotePrompts = getPromptsState?.data?.me?.profile?.prompts;
      setCurrentPrompts(
        Array.isArray(remotePrompts) ? remotePrompts : "FAILURE"
      );
    }
  }, [getPromptsState?.data, getPromptsState?.error]);

  useEffect(() => toLocalStorage(currentPrompts), [currentPrompts]); // Sync local storage against updated state

  // Utility functions returned by the hook
  const markPromptAsSeen: UseUserPrompt["markPromptAsSeen"] = useCallback(
    async (...prompts) => {
      setCurrentPrompts(
        Array.isArray(currentPrompts)
          ? [...currentPrompts, ...prompts]
          : prompts
      );

      const updatePromises = prompts.map((prompt) =>
        addPromptMutation({ variables: { prompt } }).catch((err) => {
          datadogLogs.logger.error("Error updating user prompts in UPS", {
            err,
            prompt
          });
        })
      );

      setShowNotification(true);
      await Promise.all(updatePromises);
    },
    [addPromptMutation, currentPrompts]
  );

  const shouldShowPrompt: UseUserPrompt["shouldShowPrompt"] = useCallback(
    (prompt, featureFlagCondition) => {
      const show =
        promptNotSeenBefore(prompt) &&
        isNotExcludedPage() &&
        isTargetedUserContext();

      if (show && featureFlagCondition) {
        const ffValue = ldClient?.variation(featureFlagCondition.name);
        return show && ffValue === featureFlagCondition.requiredValue;
      }

      return show;
    },
    [promptNotSeenBefore, isNotExcludedPage, isTargetedUserContext, ldClient]
  );

  return {
    shouldShowPrompt,
    markPromptAsSeen,
    showNotification,
    setShowNotification
  };
};
